import { useState, useEffect, useCallback, useRef } from 'react';

export interface ResumableCharacterInfo {
  name: string;
  role: string;
  descriptor: string;
  profile_picture_url: string;
}

export interface ResumableGameInfo {
  game_id: string;
  quest_id: string | null;
  last_saved_at: string | null;
  current_stage: number;
  current_location_name: string | null;
  character: ResumableCharacterInfo | null;
  background_image_url: string | null;
  background_video_url: string | null;
}

interface UseResumableGameResult {
  resumableGame: ResumableGameInfo | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearResumableGame: () => void;
}

/**
 * Hook to check for a resumable game, optionally filtered by quest.
 *
 * @param questId - Optional quest ID to filter by. If null/undefined, returns any resumable game.
 * @param enabled - Whether to enable the fetch (default: true)
 */
export function useResumableGame(
  questId?: string | null,
  enabled: boolean = true,
): UseResumableGameResult {
  const [resumableGame, setResumableGame] = useState<ResumableGameInfo | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Track the current abort controller to cancel stale requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Effect to fetch resumable game when questId changes
  // Uses AbortController to cancel stale requests and prevent race conditions
  useEffect(() => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!enabled) {
      setResumableGame(null);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Build URL - questId is optional
        const url = questId
          ? `/api/games/resumable?quest_id=${encodeURIComponent(questId)}`
          : '/api/games/resumable';

        const response = await fetch(url, { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`Failed to check for resumable game: ${response.status}`);
        }

        const data = await response.json();

        // Only update state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setResumableGame(data.resumable_game || null);
        }
      } catch (err) {
        // Ignore abort errors - they're expected when cancelling stale requests
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching resumable game:', err);
        if (!abortController.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setResumableGame(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup: cancel request if questId changes or component unmounts
    return () => {
      abortController.abort();
    };
  }, [questId, enabled]);

  // Manual refetch function - creates a new request for current questId
  const refetch = useCallback(async () => {
    if (!enabled) {
      setResumableGame(null);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    try {
      // Build URL - questId is optional
      const url = questId
        ? `/api/games/resumable?quest_id=${encodeURIComponent(questId)}`
        : '/api/games/resumable';

      const response = await fetch(url, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to check for resumable game: ${response.status}`);
      }

      const data = await response.json();

      if (!abortController.signal.aborted) {
        setResumableGame(data.resumable_game || null);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching resumable game:', err);
      if (!abortController.signal.aborted) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setResumableGame(null);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [questId, enabled]);

  const clearResumableGame = useCallback(() => {
    setResumableGame(null);
  }, []);

  return {
    resumableGame,
    isLoading,
    error,
    refetch,
    clearResumableGame,
  };
}
